import React from 'react';
import { useNavigate } from 'react-router-dom';
import appIcon from '../../assets/iconW.svg';
import tempIcon from '../../assets/temp.svg';
import mapIcon from '../../assets/MapIcon.svg';
import nodeIcon from '../../assets/NodeIcon.svg';
import healthIcon from '../../assets/health.svg';
import alertIcon from '../../assets/AlertIcon.svg';
import mapPreview from '../../assets/map.png';
import norahPortrait from '../../assets/team-norah.png';
import alanoudPortrait from '../../assets/team-alanoud.png';
import amaalPortrait from '../../assets/team-amaal.png';
import './LandingPage.css';

const GITHUB_URL = 'https://github.com/agalotaibi/Murjan.git';

const heroStats = [
  {
    value: '84.4%',
    label: "of the world's reefs were affected by bleaching in 2025",
  },
  {
    value: '2M',
    label: 'corals being restored at NEOM Sindalah by 2030',
  },
  {
    value: '$0',
    label: 'operational visibility tools before Murjan',
  },
];

const featureTags = [
  'NOAA CRW API',
  'NOAA Coral Reef Watch',
  'Active Coral Alerts',
  'Open-Meteo',
  'MapLibre',
  'Geo Dashboard',
  'Node Control',
  'Health Score',
];

const featureCards = [
  {
    icon: tempIcon,
    title: 'Real-time Dashboard',
    description:
      'Monitor live SST, heat stress, active alerts, bleaching risk, and environmental trends from one operational cockpit.',
    meta: ['Health Trend', 'Live Alerts'],
    tone: 'orange',
    size: 'wide',
  },
  {
    icon: mapIcon,
    title: 'Interactive Node Map',
    description:
      'View reef monitoring nodes across the Red Sea with live NOAA overlays, filters, and location context.',
    meta: ['MapLibre', 'Region Filters'],
    tone: 'teal',
    size: 'tall',
  },
  {
    icon: nodeIcon,
    title: 'Nodes Control',
    description:
      'Register virtual monitoring nodes, manage coverage, and keep region-level monitoring structured.',
    meta: ['NEOM Northern', 'Umluj 4.0 High'],
    tone: 'orange',
    size: 'compact',
  },
  {
    icon: alertIcon,
    title: 'NOAA Sync',
    description:
      'Fetch SST, anomaly, and bleaching alerts from NOAA Coral Reef Watch for every managed node.',
    meta: ['Daily Sync', 'Satellite Source'],
    tone: 'teal',
    size: 'compact',
  },
  {
    icon: healthIcon,
    title: 'Health Score',
    description:
      'Roll NOAA heat readings into a clear reef-health signal teams can use before field windows close.',
    meta: ['Roll-up Scoring', '98% Healthy'],
    tone: 'teal',
    size: 'compact',
  },
];

const workflowSteps = [
  {
    id: '01',
    title: 'Register a node',
    text: 'Operators set a Red Sea GPS coordinate. Murjan creates a virtual monitoring node without deploying hardware.',
  },
  {
    id: '02',
    title: 'Sync satellite data',
    text: 'Murjan fetches sea-surface temperature, stress levels, and bleaching pressure from NOAA.',
  },
  {
    id: '03',
    title: 'Calculate health score',
    text: 'The platform evaluates heat stress and alert conditions to generate a readable reef score.',
  },
  {
    id: '04',
    title: 'Take field action',
    text: 'Dashboards, alerts, and node summaries help teams prioritize the right intervention window.',
  },
];

const nodeSnapshots = [
  {
    name: 'NEOM',
    region: 'Northern Region (Tabuk & NEOM)',
    temp: '26.3°C',
    anomaly: '+0.69°C',
    stress: 'Low',
    status: 'Healthy',
  },
  {
    name: 'Umlj',
    region: 'Al Wajh & Umluj',
    temp: '27.7°C',
    anomaly: '+0.33°C',
    stress: 'Low',
    status: 'Active',
  },
  {
    name: '0000',
    region: 'Shushah Island',
    temp: '27.9°C',
    anomaly: '+0.80°C',
    stress: '3.82',
    status: 'Warning',
  },
];

const intelligencePillars = [
  {
    title: 'Monitor',
    text: 'Track reef health and environmental risk across Red Sea locations.',
  },
  {
    title: 'Understand',
    text: 'Translate satellite data into clear operational indicators for teams.',
  },
  {
    title: 'Act',
    text: 'Support field decisions before coral stress becomes irreversible damage.',
  },
];

const teamMembers = [
  {
    image: norahPortrait,
    name: 'Norah Alnujidi',
    role: 'Project Manager',
    bio: 'Leads the project direction, coordination, and delivery to ensure Murjan moves from idea to impact.',
  },
  {
    image: alanoudPortrait,
    name: 'Alanoud Alsmail',
    role: 'UI/UX & Business Analyst',
    bio: 'Shapes the product experience, defines the user journey, and translates complex reef operations into a clear and usable platform.',
  },
  {
    image: amaalPortrait,
    name: 'Amaal Alotaibi',
    role: 'Software Engineer',
    bio: 'Builds the technical foundation of Murjan and turns the product vision into a functional, scalable platform.',
  },
];

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="landing-page">
      <section className="landing-hero">
        <div className="landing-shell">
          <nav className="landing-nav">
            <a className="landing-brand" href="#top" aria-label="Murjan home">
              <img src={appIcon} alt="Murjan icon" />
            </a>

            <div className="landing-nav-links">
              <a href="#features">Features</a>
              <a href="#how-it-works">How it works</a>
              <a href="#about">About</a>
              <a href="#meet-the-team">Meet the Team</a>
            </div>

            <button className="landing-login" onClick={() => navigate('/login')}>
              Login
            </button>
          </nav>

          <div className="landing-hero-copy" id="top">
            <span className="landing-eyebrow">Live Red Sea monitoring</span>
            <h1>
              Protecting <span>coral reefs</span> with data.
            </h1>
            <p>
              Murjan turns satellite data and reef monitoring signals into field-ready
              decisions for coral restoration teams across the Red Sea.
            </p>

            <div className="landing-actions">
              <button className="landing-primary" onClick={() => navigate('/login')}>
                Open Murjan App
              </button>
              <a
                className="landing-secondary"
                href={GITHUB_URL}
                target="_blank"
                rel="noreferrer"
              >
                View on GitHub
              </a>
            </div>
          </div>

          <div className="landing-stats">
            {heroStats.map((stat) => (
              <article className="landing-stat-card" key={stat.value}>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </article>
            ))}
          </div>

          <p className="landing-hero-note">
            Large-scale coral restoration projects need real-time operational visibility.
            Murjan helps field teams understand reef health before the intervention window
            closes.
          </p>
        </div>
      </section>

      <section className="landing-section" id="features">
        <div className="landing-shell">
          <div className="landing-section-heading">
            <span className="landing-kicker">Features</span>
            <h2>
              Everything a reef team <span>needs</span>.
            </h2>
            <p>
              Three core capabilities built for streamlining coral monitoring,
              restoration, and observation at scale.
            </p>
          </div>

          <div className="landing-tag-row">
            {featureTags.map((tag) => (
              <span className="landing-tag" key={tag}>
                {tag}
              </span>
            ))}
          </div>

          <div className="landing-feature-grid">
            {featureCards.map((card) => (
              <article
                className={`landing-feature-card landing-feature-card--${card.size}`}
                key={card.title}
              >
                <div className={`landing-feature-icon landing-feature-icon--${card.tone}`}>
                  <img src={card.icon} alt="" />
                </div>
                <h3>{card.title}</h3>
                <p>{card.description}</p>
                <div className="landing-feature-meta">
                  {card.meta.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section landing-section--muted" id="how-it-works">
        <div className="landing-shell">
          <div className="landing-section-heading">
            <span className="landing-kicker">How it works</span>
            <h2>
              From satellite data to <span>field action</span>.
            </h2>
            <p>
              Murjan follows an operational workflow for teams managing data sources
              and reef intervention windows.
            </p>
          </div>

          <div className="landing-steps-grid">
            {workflowSteps.map((step) => (
              <article className="landing-step-card" key={step.id}>
                <span className="landing-step-index">{step.id}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section" id="operations">
        <div className="landing-shell">
          <div className="landing-section-heading">
            <h2>Operational visibility for every reef node.</h2>
            <p>
              Murjan brings reef status, environmental readings, trend data, and alerts
              into one decision-ready dashboard.
            </p>
          </div>

          <div className="landing-operations">
            <figure className="landing-workflow-image">
              <img src={mapPreview} alt="Murjan Red Sea monitoring map" />
            </figure>

            <div className="landing-node-row">
              {nodeSnapshots.map((node) => (
                <article className="landing-node-card" key={node.name}>
                  <div className="landing-node-card-top">
                    <div>
                      <strong>{node.name}</strong>
                      <span>{node.region}</span>
                    </div>
                    <em>{node.status}</em>
                  </div>

                  <div className="landing-node-metrics">
                    <div>
                      <small>Sea Surface Temp</small>
                      <strong>{node.temp}</strong>
                    </div>
                    <div>
                      <small>Anomaly</small>
                      <strong>{node.anomaly}</strong>
                    </div>
                    <div>
                      <small>Stress</small>
                      <strong>{node.stress}</strong>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="landing-section landing-section--about" id="about">
        <div className="landing-shell landing-about-grid">
          <div className="landing-about-copy">
            <span className="landing-kicker">About</span>
            <h2>
              Built for the <span>Red Sea</span>.
            </h2>
            <p>
              Murjan began with one question: what happens to a coral reef after it is
              planted? Large-scale restoration projects are growing across the Red Sea,
              but operational visibility after deployment remains limited.
            </p>
            <p>
              Instead of leaving field teams to react after bleaching damage is already
              visible, Murjan helps them monitor environmental stress across registered
              reef nodes, understand risk, and prioritize the right intervention.
            </p>

            <blockquote className="landing-quote">
              “The problem isn’t whether reefs are at risk. It’s the elimination of
              visibility from the moment you plant a reef to the moment you know whether
              it survived.”
            </blockquote>

            <div className="landing-actions">
              <a
                className="landing-secondary"
                href={GITHUB_URL}
                target="_blank"
                rel="noreferrer"
              >
                GitHub Repository
              </a>
              <button className="landing-primary" onClick={() => navigate('/login')}>
                Live Platform
              </button>
            </div>
          </div>

          <div className="landing-about-side">
            <article className="landing-side-card">
              <span className="landing-kicker landing-kicker--card">Domain Expert</span>
              <h3>Dr. Hamed Al-Bougi</h3>
              <p>
                Biomedical engineer and coral reef research specialist whose operational
                framing shaped Murjan’s restoration-first workflow.
              </p>
            </article>

            <article className="landing-side-card">
              <span className="landing-kicker landing-kicker--card">Tech Stack</span>
              <div className="landing-side-tags">
                <span>React</span>
                <span>MapLibre GL JS</span>
                <span>Python FastAPI</span>
                <span>PostgreSQL + PostGIS</span>
                <span>Docker</span>
                <span>NOAA CRW API</span>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="landing-section landing-section--tight">
        <div className="landing-shell">
          <div className="landing-section-heading">
            <h2>From monitoring to restoration intelligence.</h2>
            <p>
              Murjan is designed to become an operational layer for coral restoration
              teams, connecting environmental data, reef nodes, alerts, and field
              decisions into one platform.
            </p>
          </div>

          <div className="landing-pillars">
            {intelligencePillars.map((pillar) => (
              <article className="landing-pillar-card" key={pillar.title}>
                <span className="landing-pillar-dot" />
                <h3>{pillar.title}</h3>
                <p>{pillar.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section landing-team" id="meet-the-team">
        <div className="landing-shell">
          <div className="landing-section-heading landing-team-heading">
            <h2>Meet the Team</h2>
            <p>
              The minds behind Murjan bringing together product thinking, design, and
              engineering to build a smarter future for coral restoration.
            </p>
          </div>

          <div className="landing-team-grid">
            {teamMembers.map((member) => (
              <article className="landing-team-card" key={member.name}>
                <div className="landing-team-avatar">
                  <img src={member.image} alt={member.name} />
                </div>

                <span className="landing-team-name">{member.name}</span>
                <h3>{member.role}</h3>
                <p>{member.bio}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-cta">
        <div className="landing-shell landing-cta-shell">
          <div className="landing-section-heading landing-section-heading--center">
            <h2>
              See the reef. <span>Save the reef.</span>
            </h2>
            <p>Murjan is live and monitoring the Red Sea.</p>
          </div>

          <div className="landing-actions landing-actions--center">
            <button className="landing-primary" onClick={() => navigate('/login')}>
              Open Murjan App
            </button>
            <a
              className="landing-secondary"
              href={GITHUB_URL}
              target="_blank"
              rel="noreferrer"
            >
              View on GitHub
            </a>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        Powered by NOAA Coral Reef Watch. Built for Red Sea restoration teams.
      </footer>
    </div>
  );
};

export default LandingPage;
